/**
 * 일반 검색 결과 0건 화면(09-29 미리보기 확정안)의 원인 판정 · 지도 넓히기 범위.
 *
 * 원인은 "결과가 생길 가능성이 가장 큰 행동" 순서로 고른다 — 필터 → 검색어 → 지역.
 * 여러 원인이 겹치면 첫째가 큰 버튼(주 행동), 나머지는 보조 버튼이 된다.
 *  - 필터: 가격·이용 방식·카테고리. 검색어·지역·날짜는 필터가 아니다.
 *  - 검색어: ?q(레거시 ?search 포함)
 *  - 지역: 범위(bounds) 검색이거나 도시·동네·나라·내 주변 검색
 */
import type { SearchBounds } from "@/lib/search/search-state";

export type SearchEmptyCause = "filters" | "keyword" | "area";

export interface SearchEmptyInput {
  /** 가격·이용 방식·카테고리 중 하나라도 걸려 있는가 */
  filtersActive: boolean;
  keyword: string | null | undefined;
  /** 범위 검색이거나 지역(도시·동네·나라·내 주변)으로 좁힌 검색인가 */
  areaScoped: boolean;
}

/** 우선순위 순서의 원인 목록. 비면 원인을 특정할 수 없는 0건(버튼 없는 기본 문구). */
export function searchEmptyCauses(input: SearchEmptyInput): SearchEmptyCause[] {
  const causes: SearchEmptyCause[] = [];
  if (input.filtersActive) causes.push("filters");
  if (input.keyword && input.keyword.trim()) causes.push("keyword");
  if (input.areaScoped) causes.push("area");
  return causes;
}

/** 지역으로 좁힌 검색의 scope — keyword·partner 는 지역 검색이 아니다. */
const AREA_SCOPES = new Set(["city", "district", "country", "nearby"]);

export function isAreaScopedSearch(params: {
  hasBounds: boolean;
  scope: string | null | undefined;
}): boolean {
  return params.hasBounds || AREA_SCOPES.has(params.scope ?? "");
}

/** 제목에 넣는 검색어 — 길면 자른다(코드 포인트 기준, 이모지·한자가 반으로 갈리지 않게). */
export function truncateKeyword(keyword: string, max = 20): string {
  const chars = Array.from(keyword.trim());
  return chars.length > max ? `${chars.slice(0, max).join("")}…` : chars.join("");
}

const MAX_LAT = 85;

// 주소에 싣는 좌표 — 소수 5자리(≈1m)면 충분하다.
const round5 = (v: number) => Math.round(v * 1e5) / 1e5;

function clampBounds(center: { lat: number; lng: number }, halfLat: number, halfLng: number): SearchBounds {
  const hLng = Math.min(180, halfLng);
  return {
    ne_lat: round5(Math.min(MAX_LAT, center.lat + halfLat)),
    sw_lat: round5(Math.max(-MAX_LAT, center.lat - halfLat)),
    ne_lng: round5(Math.min(180, center.lng + hLng)),
    sw_lng: round5(Math.max(-180, center.lng - hLng)),
  };
}

/**
 * 중심·줌 → 대략 한 화면 범위. 서버 search-data 의 boundsFromMapView 와 같은 관계
 * (half-span도 = 180 / 2^zoom)를 쓴다 — 지도 인스턴스 없이 줌으로만 범위를 낼 때.
 */
export function boundsFromView(center: { lat: number; lng: number }, zoom: number): SearchBounds {
  const half = 180 / 2 ** Math.max(0, zoom);
  return clampBounds(center, half, half);
}

/**
 * "지도 넓혀 검색" 범위 — 지금 보는 지도에서 줌을 `steps` 단계 뺀 범위.
 *
 * 지도 인스턴스는 MapView 안에 있어 여기서는 주소창의 값으로 계산한다:
 *  1. 뷰(clat·clng·zoom)가 있으면 = 사용자가 지금 보고 있는 자리 → 그 줌 − steps 의 범위
 *  2. 없고 검색 범위(bounds)만 있으면 → 그 범위를 같은 중심으로 2^steps 배
 *  3. 둘 다 없으면 → 페이지가 잡은 지도 중심·초기 줌
 */
export function widenedSearchBounds(
  input: {
    view: { clat?: number; clng?: number; zoom?: number };
    bounds: SearchBounds | null | undefined;
    fallbackCenter: { lat: number; lng: number };
    fallbackZoom: number;
  },
  steps = 2,
): SearchBounds {
  const { view, bounds } = input;
  if (view.clat !== undefined && view.clng !== undefined && view.zoom !== undefined) {
    return boundsFromView({ lat: view.clat, lng: view.clng }, view.zoom - steps);
  }
  if (bounds) {
    const factor = 2 ** steps;
    const center = { lat: (bounds.ne_lat + bounds.sw_lat) / 2, lng: (bounds.ne_lng + bounds.sw_lng) / 2 };
    const halfLat = (Math.abs(bounds.ne_lat - bounds.sw_lat) / 2) * factor;
    const halfLng = (Math.abs(bounds.ne_lng - bounds.sw_lng) / 2) * factor;
    return clampBounds(center, halfLat, halfLng);
  }
  return boundsFromView(input.fallbackCenter, input.fallbackZoom - steps);
}

// ===== 0건 화면 아래 상품(09-29) — "항상 무언가를 보여 준다" =====

/** 서버 폴백의 출처 — 제목이 갈린다(area/country = "○○ 추천 상품", popular = "지금 인기 있는 상품") */
export type FallbackKind = "area" | "popular";

/**
 * 서버 폴백을 어디서 뽑는가.
 *  - 나라 검색 → 그 나라 전역
 *  - 범위(검색 범위·지도 뷰에서 유도한 범위)가 있으면 → 그 범위(키워드 없이, 점진 확장)
 *  - 범위도 나라도 없는 검색어 검색 → 전체 인기 상품(get_search_top_picks)
 *  - 파트너 스토어 검색은 다른 업체 상품을 끼우지 않는다 → 없음
 */
export function zeroResultFallbackSource(input: {
  hasCountry: boolean;
  hasBounds: boolean;
  isPartner: boolean;
}): "country" | "area" | "popular" | "none" {
  if (input.hasCountry) return "country";
  if (input.hasBounds) return "area";
  if (input.isPartner) return "none";
  return "popular";
}

/**
 * 폴백 조회의 이용 방식 조건 — 이용 방식은 **뺀다**. 이용 방식 때문에 0건이면 같은 조건의
 * 폴백도 0건이라 화면이 비기 때문이다. 택배(delivery)의 delivery_country 는 택배 조건과
 * 한 짝이라 함께 뺀다. 나라 검색의 나라(=지역 조건)는 그대로 둔다.
 */
export function fallbackUsageFilter(
  usageMethod: string | undefined,
  deliveryCountry: string | undefined,
): { usageMethod: undefined; deliveryCountry: string | undefined } {
  return {
    usageMethod: undefined,
    deliveryCountry: usageMethod === "delivery" ? undefined : deliveryCountry,
  };
}

export type ZeroResultListKind = FallbackKind | "unfiltered";

/**
 * 화면(클라이언트)에서 0건일 때 아래에 깔 상품의 출처. 결과가 보이면 null.
 * 서버 결과는 있는데 클라이언트 필터(가격·카테고리)로 0건이 됐으면 — 서버 폴백은 비어
 * 있다 — 필터 전 서버 결과를 그대로 보여 준다("필터를 풀면 볼 수 있는 상품"). 요청 0.
 * 아니면 서버가 준 폴백. 개수만 받는다(배열을 넘기면 React Compiler 가 호출 쪽 메모를
 * 인자 변형 가능성으로 보고 포기한다).
 */
export function zeroResultListKind(input: {
  visibleCount: number;
  unfilteredCount: number;
  serverKind: FallbackKind;
}): ZeroResultListKind | null {
  if (input.visibleCount > 0) return null;
  return input.unfilteredCount > 0 ? "unfiltered" : input.serverKind;
}

/** 0건 화면 아래 상품 상한 — 모바일 2열·데스크톱 3열 모두 마지막 줄이 찬다 */
export const ZERO_RESULT_LIST_LIMIT = 12;
